/**
 * Role helpers over users/{uid}.roles (any case; unknown values dropped).
 *
 * Subscription tier is not read from the user object any more: it comes from
 * subscriptions/{uid} (useSubscription / hasFeatureAccess). The user-object
 * tier helpers that were here had no callers and ignored subscriptionStatus,
 * so a canceled plan would have read as paid; they were removed with their
 * tests on 2026-10-09.
 */
import { normalizeRolesToCanonical } from "../../shared/user-roles";

/**
 * Normalizes role field to always be an array of uppercase strings
 */
export function normalizeRoles(user: any): string[] {
  const roleData = user?.roles ?? user?.profile?.roles ?? [];
  const rolesArray = Array.isArray(roleData) ? roleData : [roleData];
  
  const upper = rolesArray
    .filter((role: any) => role && typeof role === "string")
    .map((role: string) => role.toUpperCase());

  const normalized = normalizeRolesToCanonical(upper);

  return normalized;
}

/**
 * Checks if user has a specific role
 */
export function hasRole(user: any, role: string): boolean {
  const roles = normalizeRoles(user);
  return roles.includes(role.toUpperCase());
}

/**
 * Checks if user has any of the specified roles
 */
export function hasAnyRole(user: any, rolesToCheck: string[]): boolean {
  const roles = normalizeRoles(user);
  const normalizedRolesToCheck = rolesToCheck
    .filter(r => r && typeof r === 'string')
    .map(r => r.toUpperCase());
  return roles.some(role => normalizedRolesToCheck.includes(role));
}

/**
 * Checks if user is an admin
 */
export function isAdmin(user: any): boolean {
  return hasAnyRole(user, ["ADMIN"]);
}

/**
 * Checks if user is an instructor
 */
export function isInstructor(user: any): boolean {
  return hasAnyRole(user, ["INSTRUCTOR", "ADMIN"]);
}

/**
 * Checks if user can edit content
 */
export function canEditContent(user: any): boolean {
  return hasAnyRole(user, ["ADMIN", "INSTRUCTOR"]);
}
